import { ChangeDetectorRef, Directive, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild, ViewRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IRequestCallback, IRequestOptions, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { Subscription } from 'rxjs';
import { Customer } from '../api/customer';
import { NeedLoginService } from '../need-login.service';
import { Login } from './login.interfaces';
import { NSession } from '../df-session.config';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class LoginFormComponentDef<
        L extends Login.ILoginFormData = Login.ILoginFormData,
        R extends Login.IRegisterFormData = Login.IRegisterFormData,
        P extends Login.IResetPasswordFormData = Login.IResetPasswordFormData
    >
    implements OnInit, OnDestroy
{
    @Input()
    source?: string;

    /**
     * Login action status
     */
    loginStatus = new Status();

    /**
     * Check for account action status
     */
    checkStatus = new Status();

    /**
     * Account create action status
     */
    registerStatus = new Status();

    /**
     * Reset password action status
     */
    passwordStatus = new Status();

    /**
     * Result of checking for account related to current email
     */
    checkResult?: Login.IResponseAccountExists;

    /**
     * Subscriptions wrapper
     */
    protected subscriptions = new Subscription();

    /**
     * Last email value
     */
    tmpEmail?: string;

    protected emailFieldName?: string = 'email';

    private dfSessionOptions = inject(NSession.OPTIONS);

    /**
     * Form
     * important: you need to put #form="ngForm" on form element in template
     */
    @ViewChild('form', { static: false })
    public form?: NgForm;

    @ViewChild('form', { read: ElementRef })
    public formRef?: ElementRef;

    @ViewChild('emailInput', { read: ElementRef })
    public emailInput?: ElementRef<HTMLInputElement>;

    /**
     * Local component data
     * @type {Object}
     */
    protected _data: Partial<L | R | P> = {};

    private formSuscription?: Subscription;
    /**
     * Data setter
     */
    set data(data: Partial<L | R | P>) {
        const email = data?.email;
        data ??= {};
        this.onDataChange();
        // temp set prev not to make email setter work correctly
        data.email = this.email;
        this._data = data;
        this.email = email;
    }

    /**
     * Data getter
     */
    get data(): Partial<L | R | P> {
        return this._data;
    }

    private onDataChange() {
        if (this.data && !('newsletter' in this.data)) {
            const defaultNewsletter = this.dfSessionOptions.newsletter.default;
            if (typeof defaultNewsletter === 'boolean') {
                (this.data as R).newsletter = defaultNewsletter ? 1 : 0;
            }
        }
    }

    /**
     * Email setter
     */
    set email(email: string | undefined) {
        const oldEmail = this.data.email;
        if (oldEmail !== email) {
            this.tmpEmail = this.data.email = email;
            this.onEmailChange(email, oldEmail);
        }
    }

    /**
     * Email getter
     */
    get email(): string | undefined {
        return this.data.email;
    }

    constructor(
        protected ref: ChangeDetectorRef,
        protected request: RequestService,
        readonly customer: Customer,
        readonly app: App,
        readonly needLoginService: NeedLoginService
    ) {}

    ngOnInit() {
        this.subscriptions.add(this.customer.model.obs.data.subscribe(() => this.onCustomerDataChange()));
        this.onDataChange();
    }

    ngOnDestroy() {
        this.formSuscription?.unsubscribe();
        this.subscriptions.unsubscribe();
        // we have to clear need login to prevent situation:
        // need login action (wishlist) => redirect to login
        // we leave login page without log in
        // we come back to login page, log in and need login action is executed
        this.needLoginService.clearNeedLogin();
    }

    /**
     * Check results setter
     * Check result is the result of request checking if customer exits
     */
    protected setCheckResult(checkResult?: Login.IResponseAccountExists) {
        this.checkResult = checkResult;
        this.detectChanges();
    }

    protected getCheckData(): Login.IRequestAccountExists {
        return { email: this.email || '' };
    }

    protected onEmailChange(newEmail: string | undefined, oldEmail: string | undefined): void {
        this.passwordStatus.reset();

        newEmail ? this.check(true) : this.setCheckResult();
    }

    applyTmpEmail(force = false) {
        // on blur, entr we should force from template, on any other change when we are not in the input it should go automatically
        if (force || (this.emailInput && this.emailInput.nativeElement !== document.activeElement)) {
            this.email = this.tmpEmail;
        }
    }

    check(silent = false) {
        this.checkStatus.busy = true;
        this.setCheckResult();

        if (!this.form) {
            return;
        }
        // we may validate in async way, once form is valid we need to recheck as the origianl check request was not sent
        this.formSuscription?.unsubscribe();
        if (!this.checkEmailIsValid()) {
            this.formSuscription = this.form?.statusChanges!.subscribe(() => {
                if (this.checkEmailIsValid()) {
                    this.formSuscription?.unsubscribe();
                    this.check(silent);
                }
            });
            if (this.form) {
                if (!silent) {
                    this.form.submitAttempt = true;
                }
                this.form.ngSubmit.emit();
            }
            this.checkStatus.busy = false;
            this.detectChanges();
            return;
        }
        const currentEmail = this.email;
        this.checkRequest(currentEmail);
    }

    protected checkRequest(email?: string) {
        this.request.send(
            this.checkStatus,
            () => {
                return this.exists();
            },
            result => {
                this.onCheckRequestSuccess(result, email);
            },
            error => {
                this.onCheckRequestError(error);
            }
        );
    }

    protected async exists() {
        return this.request.nexus.customer.exists(this.getCheckData());
    }

    protected onCheckRequestSuccess(result: Login.IResponseAccountExists, email?: string) {
        if (this.form) {
            this.form.submitAttempt = false;
        }
        this.checkStatus.busy = false; // we need this dropped before setting check results
        if (this.email === email) {
            // only if still the same email
            this.setCheckResult(result);
        }
    }

    protected onCheckRequestError(error: any) {}

    /**
     * Tries to check if email address is valid using form or email field validation
     */
    private checkEmailIsValid() {
        if (this.form) {
            if (this.emailFieldName) {
                const emailField = this.form.controls[this.emailFieldName];
                if (emailField) {
                    return !(emailField.invalid || emailField.pending);
                }
            }
            return this.form.valid;
        }
        return !!this.email;
    }

    protected formExists(
        form: NgForm,
        status: Status,
        data: Login.IRequestAccountExists,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Login.IResponseAccountExists> | void {
        return this.request.submitForm(form, status, () => this.request.nexus.customer.exists(data as L), onSuccess, onError);
    }

    isChecked(): boolean {
        return !!this.checkResult;
    }

    hasAccount(): boolean {
        return !!this.checkResult && this.checkResult.exists;
    }

    shouldLogin(): boolean {
        return !this.isLoggedIn && this.isChecked() && this.hasAccount();
    }

    shouldRegister(): boolean {
        return !this.isLoggedIn && this.isChecked() && !this.hasAccount();
    }

    login(onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.submitForm(
            this.form,
            this.loginStatus,
            () => this.request.nexus.customer.login(this.data as L),
            onSuccess,
            onError
        );
    }

    register(onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.submitForm(
            this.form,
            this.registerStatus,
            () => this.request.nexus.customer.register(this.getRegisterData()),
            onSuccess,
            onError,
            this.getRegisterRequestOptions(),
            { errorScrollContainer: this.formRef?.nativeElement }
        );
    }

    protected getRegisterData(): R {
        const data = { ...this.data } as R;
        if (this.source && data.newsletter) {
            data.source = this.source;
        }
        return data;
    }

    protected getRegisterRequestOptions(): IRequestOptions | undefined {
        return undefined;
    }

    get isLoggedIn(): boolean {
        return this.customer.model.isLoggedIn;
    }

    resetPassword(email: string = this.data.email || '') {
        const form = this.form;
        if (form) {
            if (form.controls['email'].invalid) {
                if (!form.submitAttempt) {
                    form.submitAttempt = true;
                    form.ngSubmit.emit();
                }
                return;
            }
        }
        this.request.send(
            this.passwordStatus,
            () => this.request.nexus.customer.requestPasswordReset({ email }),
            () => this.onResetPasswordSuccess()
        );
    }

    protected onResetPasswordSuccess() {}

    detectChanges() {
        if (!(<ViewRef>this.ref).destroyed) {
            this.ref.detectChanges();
        }
    }

    protected onCustomerDataChange(): void {
        this.detectChanges();
    }

    canSubscribe(): boolean {
        if (this.checkResult) {
            return !this.checkResult.newsletter;
        }
        return false;
    }
}
